import type { UserRole } from '@atlas/types'
import { useMemo, useState } from 'react'
import type { CampaignStatus } from '@/domain/campaign.types'
import { isCampaignError } from '@/domain/campaign-errors'
import { useAuth } from '@/hooks/useAuth'
import {
	useCampaign,
	useCampaignAvailableActions,
	useDeleteCampaign,
	useTransitionCampaign,
} from '@/hooks/useCampaigns'
import { campaignCapabilitiesFor } from '@/lib/campaign-capabilities'

// The Campaign detail container's data-composition layer — the sole consumer of
// the detail/lifecycle/delete hooks for the detail screen. Available actions
// arrive as data from the lifecycle capability; this container performs no
// derivation of its own. Delete invocation is guarded so a second confirmation
// while the first is still pending never reaches the command boundary twice.

export type CampaignTransitionViewState = 'idle' | 'pending' | 'success' | 'invalid-transition' | 'conflict' | 'failure'

/** Derives the panel's view state from the transition mutation's own state — never from the Campaign's status. */
function transitionViewStateOf(mutation: {
	isPending: boolean
	isError: boolean
	isSuccess: boolean
	error: unknown
}): CampaignTransitionViewState {
	if (mutation.isPending) return 'pending'
	if (mutation.isSuccess) return 'success'
	if (!mutation.isError) return 'idle'
	if (isCampaignError(mutation.error) && mutation.error.category === 'invalid-transition') return 'invalid-transition'
	if (isCampaignError(mutation.error) && mutation.error.category === 'conflict') return 'conflict'
	return 'failure'
}

export function useCampaignDetailContainer(campaignId: string, role: UserRole | string, onDeleted?: () => void) {
	const { user } = useAuth()
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

	const detail = useCampaign(campaignId)
	const availableActions = useCampaignAvailableActions(campaignId)
	const transitionMutation = useTransitionCampaign()
	const deleteMutation = useDeleteCampaign()

	const capabilities = useMemo(
		() => campaignCapabilitiesFor(role, { userId: user?.userId, ownerId: detail.data?.createdBy }),
		[role, user?.userId, detail.data?.createdBy],
	)
	const transitionViewState = transitionViewStateOf(transitionMutation)

	function onTransition(to: CampaignStatus) {
		transitionMutation.mutate({ campaignId, to })
	}

	function onOpenDeleteDialog() {
		setIsDeleteDialogOpen(true)
	}

	function onCancelDelete() {
		setIsDeleteDialogOpen(false)
	}

	function onConfirmDelete() {
		if (deleteMutation.isPending) return
		deleteMutation.mutate(campaignId, { onSuccess: onDeleted })
	}

	function onReloadCampaign() {
		const reloadCampaign = detail.refetch
		void reloadCampaign()
	}

	return {
		campaign: detail.data,
		isLoading: detail.isLoading,
		isError: detail.isError,
		isNotFound: isCampaignError(detail.error) && detail.error.category === 'not-found',
		availableActions: availableActions.data ?? [],
		capabilities,
		transitionViewState,
		onTransition,
		onReloadCampaign,
		isDeleteDialogOpen,
		deleteViewState: deleteMutation.isPending ? 'pending' : deleteMutation.isError ? 'failure' : 'idle',
		onOpenDeleteDialog,
		onCancelDelete,
		onConfirmDelete,
	}
}
